// app-pages/PaymentsPage/PaymentsPage.tsx
"use client";
import { GoogleForm, Jumbotron, ResponsiveStack } from "components";
import {
  Text,
  Button,
  Flex,
  Box,
  ListItem,
  UnorderedList,
  useDisclosure,
  Image,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  useToast
} from "@chakra-ui/react";
import PaymentsModal from "./PaymentsModal";
import   {useState, useEffect } from "react";
import { useAppContext } from "hooks";
import { AddIcon, MinusIcon } from "@chakra-ui/icons";
import Link from "next/link";
import Bitcoin from "@public/Bitcoin 1.png";
import { fetch as customFetch, loadPayPalScript } from "libs";
import { POST_TRACK_USER_SALE } from "api";
import { useRouter } from "next/navigation";


declare const gtag: Function;

export const PaymentsPage = () => {
  const plans = [
    {
      credits: "50",
      finalPrice: "9.99",
      borderColor: "#712EFF",
      buttonBackground: "#712EFF",
      textColor: "#712EFF",
      perks: ["0.2$ per image", "Unlock Model Training", "Unlock faster generations ~2 seconds"],
      name: "basic",
    },
    {
      credits: "200",
      price: "39.99",
      finalPrice: "24.99",
      borderColor: "#FF1B6D",
      buttonBackground: "#FF1B6D",
      textColor: "#FF1B6D",
      perks: [
        <Text>
          $0.13 per image - <span style={{ fontWeight: "bold" }}>35% Discount</span>
        </Text>,
        "Unlock Model Training",
        "Unlock faster generations ~2 seconds",
      ],
      name: "mid",
    },
    {
      credits: "500",
      price: "99.99",
      finalPrice: "49.99",
      borderColor: "#712EFF",
      buttonBackground: "#712EFF",
      textColor: "#712EFF",
      perks: [
        <Text>
          Only $0.1 per image - <span style={{ fontWeight: "bold" }}>50% Discount</span>
        </Text>,
        "Unlock Model Training",
        "Unlock faster generations ~2 seconds",
      ],
      isGradient: true,
      name: "expert",
    },
  ];

  return (
    <Flex align="center" direction="column">
      <h1 style={{ position: 'absolute', left: '-9999px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }}>
      DeepMode Payments | Recharge Your AI Art Creation Credits
    </h1>
      <Jumbotron height="100%">
        <Flex textAlign="center" h="300px" direction="column" justify="center" align="center">
          <Text
            style={{
              background: "linear-gradient(to right,  white, 70%, #A4DBEE)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              MozBackgroundClip: "text",
            }}
            fontSize="56px"
            fontWeight="extrabold"
          >
            DiveDeep
          </Text>
          <Text>Unlock the power of DeepMode with generations credits</Text>
        </Flex>
      </Jumbotron>

      <ResponsiveStack gap={4}>
        {plans.map((plan, index) => (
          <Box
            key={index}
            p="1px"
            borderRadius="12px"
            bgGradient={plan.isGradient ? "linear(to-r, #FFB5FF, #1AA5D3)" : "none"}
          >
            <PaymentCard
              buttonBackground={plan.buttonBackground}
              textColor={plan.textColor}
              borderColor={plan.borderColor}
              perks={plan.perks}
              finalPrice={plan.finalPrice}
              price={plan.price}
              credits={plan.credits}
              isGradient={plan.isGradient}
              name={plan.name}
            />
          </Box>
        ))}
      </ResponsiveStack>
      <Text mt="20px">
        Need a bigger custom plan?{" "}
        <span style={{ fontWeight: 700, textDecoration: "underline", color: "#FF1B6D" }}>
          <a href="mailto:info@deepmode.com">Contact us</a>
        </span>
      </Text>
      <CustomAccordion />
      <Box h="100px" />
    </Flex>
  );
};

const PaymentCard = ({
  buttonBackground,
  textColor,
  borderColor,
  perks,
  finalPrice,
  price,
  credits,
  isGradient,
  name,
}: any) => {
  const { profile } = useAppContext();
  const gradient = "linear(to-r, #FFB5FF, #1AA5D3)";
  const [isLoginModalOpen, setIsLoginModalOpen] = useState<boolean>(false);
  const { isOpen: isPaymentsOpen, onClose: onClosePayments, onOpen } = useDisclosure();
  const toast = useToast();
  const router = useRouter();

  useEffect(() => {
    loadPayPalScript();
    if (isPaymentsOpen) {
      setTimeout(() => {
        handlePaypalCheckout();
      }, 500);
    }
  }, [isPaymentsOpen]);
  const getCookieValue = (name: string) => {
    const nameString = name + "=";
    const value = document.cookie.split("; ").find((row) => row.startsWith(nameString));
    if (value) {
      return value.split("=")[1];
    }
    return null;
  };

  const handlePaypalCheckout = () => {
    const userId = profile?.id;
  
    if (!(window as any).paypal) {
      console.error("PayPal SDK not loaded yet");
      return;
    }
  
    const containerId = `${name}-button-container`;
  
    // Ensure PayPal button is not re-rendered if it already exists
    if (document.querySelector(`#${containerId} iframe`)) {
      return;
    }
  
    let lastProcessedTransactionId: string | null = null;
  
    (window as any).paypal
      .Buttons({
        // Specify PayPal as the only funding source to hide the credit card option
        fundingSource: (window as any).paypal.FUNDING.PAYPAL,
        createOrder: function (_: any, actions: any) {
          return actions.order.create({
            purchase_units: [
              {
                amount: {
                  value: finalPrice.toString(),
                },
                description: name,
                custom_id: JSON.stringify({ user_id: userId, plan_name: name }),
              },
            ],
          });
        },
        onApprove: function (_: any, actions: any) {
          return actions.order.capture().then(function (details: any) {
            if (details.id === lastProcessedTransactionId) {
              console.log("This transaction has already been processed.");
              return;
            }
  
            lastProcessedTransactionId = details.id;
  
            // Payment success logic
            toast({
              title: "Payment Successful",
              description: "Thank you for your payment! You should see your credit soon",
              status: "success",
              duration: 5000,
              isClosable: true,
            });
  
            // Google Analytics event for tracking purchase
            gtag("event", "purchase", {
              transaction_id: details.id,
              value: finalPrice,
              currency: "USD",
              payment_method: "PayPal",
              items: [
                {
                  id: name,
                  name: name,
                  quantity: 1,
                  price: finalPrice,
                },
              ],
            });
  
            // Custom fetch to your server for further processing
            customFetch(POST_TRACK_USER_SALE, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                tid: getCookieValue("_fprom_tid"),
                amount: finalPrice * 100, // Convert to cents if necessary
                email: details.payer.email_address,
              }),
            })
              .then((response) => response.json())
              .then((data) => {
                if (data.success) {
                  console.log("Sale tracked with FirstPromoter successfully.");
                } else {
                  console.error("Failed to track sale with FirstPromoter:", data.message);
                }
              })
              .catch((error) => {
                console.error("Error calling track_sale endpoint:", error);
              });
  
            // Navigate to the success page after a short delay
            setTimeout(() => {
              router.push("/payments/success");
            }, 2000);
          });
        },
      })
      .render(`#${containerId}`); // Render PayPal button in the specified container
  };
  

  return (
    <Flex
      py="26px"
      px="12px"
      h={{ base: "430px", lg: "405px" }}
      w={{ base: "320px", lg: "395px" }}
      borderRadius="12px"
      align="center"
      direction="column"
      bg="#16181F"
      border="1px solid"
      borderColor={isGradient ? "transparent" : borderColor}
      position="relative"
    >
      <GoogleForm isOpen={isLoginModalOpen} onClose={() => setIsLoginModalOpen(false)} />
      {name === "expert" && (
        <Image w="25px" position="absolute" right="2" top="2" src={Bitcoin.src} />
      )}

      <Flex mt="15px" align="end">
        <Text color={textColor} lineHeight={0.8} fontSize="72px" fontWeight="bold">
          {credits}
        </Text>
        <Text fontWeight="semibold">Credits</Text>
      </Flex>
      <Flex mt="35px" h="1px" bg="#2B2B2C" w="100%" />
      <Flex mt="15px">
        {price && (
          <Text fontSize="26px" textDecoration="line-through" color="#626262">
            ${price}
          </Text>
        )}{" "}
        <Text ml="3px" fontWeight="semibold" fontSize="26px">
          ${finalPrice}
        </Text>
      </Flex>
      <Flex mt="15px" h="1px" bg="#2B2B2C" w="100%" />

      <Button
        w="100%"
        color="white"
        mt="25px"
        bg={isGradient ? "none" : buttonBackground}
        bgGradient={isGradient ? gradient : "none"}
        minH="40px"
        onClick={() => {
          gtag("event", "start_payment", {
            event_category: "Payment",
            event_label: "Initiate Payment",
            plan: { name },
            value: { price },
          });
          if (!profile) {
            setIsLoginModalOpen(true);
            return;
          }
          onOpen();
        }}
      >
        Get Credits
      </Button>
      <PaymentsModal
        price={finalPrice}
        isPaymentsOpen={isPaymentsOpen}
        onClosePayments={onClosePayments}
        plan={name}
        onPaypalCheckout={handlePaypalCheckout}

      />
      <UnorderedList
        mt="30px"
        styleType="none"
        display="flex"
        flexDirection="column"
        alignItems="start"
      >
        {perks.map((perk: string, index: number) => (
          <ListItem key={index} display="flex" alignItems="center">
            <Box as="span" mr="8px" w="5px" h="5px" borderRadius="50%" bg="currentColor" />{" "}
            {/* Custom bullet */}
            {perk}
          </ListItem>
        ))}
      </UnorderedList>
    </Flex>
  );
};

const CustomAccordion = () => {
  const faqData = [
    {
      question: "What is DeepMode and how does it work?",
      answer:
        "DeepMode is a Generative AI platform that helps creators make their AI clones. Creators can generate beautiful AI photos, whether realistic or non-realistic, with unlimited freedom. With DeepMode, creators can unleash their imagination and create unique photos with unlimited freedom.",
    },
    {
      question: "How much does DeepMode cost?",
      answer: (
        <Text>
          DeepMode offers various top-up plans: Check{" "}
          <Link style={{ textDecoration: "underline" }} href="/payments">
            this page
          </Link>{" "}
          for more details.
        </Text>
      ),
    },
    {
      question: "Can I try DeepMode for free?",
      answer:
        "Yes, every new user automatically gets 20 credits. However, to unlock training an AI model you will need to top-up your balance.",
    },
    {
      question: "Are my DeepMode creations private?",
      answer:
        "Yes, your creations on DeepMode are kept private and only you can view them. You also have the option to delete specific models or images you don’t like.",
    },
    {
      question: "Does DeepMode store my uploaded images?",
      answer:
        "No, your uploaded photos are deleted immediately from our servers after training. In addition, the Model itself is being deleted from our servers after 30 days. If you wish to extend the model storage, you can do so.",
    },
    {
      question: "Can I use my creations anywhere?",
      answer: "Yes. Your generated images are yours to use on any other platform.",
    },
    {
      question: "How is DeepMode different from Midjourney, Stable Diffusion, or Dall-E?",
      answer:
        "Unlike other AI platforms such as Midjourney, Stable Diffusion, or DALL-E, DeepMode stands out for its exclusive focus on the generation of AI clones. DeepMode is committed to providing a fully uncensored experience, allowing users unlimited creative freedom without any content restrictions.",
    },
    {
      question: "Does DeepMode run on the Cloud?",
      answer:
        "Yes, DeepMode runs in the cloud. This means that you can use the platform and view your generations across various devices.",
    },
    {
      question: "Does DeepMode offer an Affiliate program?",
      answer:
        "Absolutely! Become a partner and earn cash rewards every month. Find out more about our Affiliate Program.", // Add the actual link
    },
    {
      question: "Can I create videos with DeepMode?",
      answer:
        "Currently, video generation is not supported. However, we’re working on this feature and plan to introduce it soon.",
    },
    {
      question: "Can I get a refund?",
      answer:
        "We offer refunds for purchases made within the last 7 days, provided no credits have been used. If you believe you’re eligible for a refund, please contact us here.", // Add the actual link
    },
    {
      question: "How do I get in touch with the DeepMode team?",
      answer: (
        <Text>
          We usually hangout on{" "}
          <a style={{ textDecoration: "underline" }} href="https://discord.gg/CMwtccKthy">
            Discord
          </a>
          . You can also reach out to us via{" "}
          <a style={{ textDecoration: "underline" }} href="mailto:info@deepmode.com">
            e-mail
          </a>
          .
        </Text>
      ),
    },
  ];

  return (
    <Flex
      mt="250px"
      mb="150px"
      direction="column"
      w="100%"
      px={["35px", "35px", "40px", "60px", "60px", "15%"]}
    >
      <Text fontSize="40px" fontWeight="semibold">
        FAQ
      </Text>
      <Accordion mt="10px" allowToggle>
        {faqData.map((item, index) => (
          <AccordionItem key={index}>
            {({ isExpanded }) => (
              <>
                <AccordionButton>
                  <Box flex="1" textAlign="left">
                    {item.question}
                  </Box>
                  {isExpanded ? <MinusIcon fontSize="12px" /> : <AddIcon fontSize="12px" />}
                </AccordionButton>
                <AccordionPanel pb={4}>{item.answer}</AccordionPanel>
              </>
            )}
          </AccordionItem>
        ))}
      </Accordion>
    </Flex>
  );
};

export default PaymentsPage;
